import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FilterPanel } from '@gsa/afp-component-library';
import { useFederalStandards } from '../../../fvs-provider';

const {
  FilterPanelField,
  FilterPanelFilterShape,
} = FilterPanel;

const SELECT_VEHICLE_TYPE = { label: 'Select vehicle type', value: '' };

const VehicleTypeFilterItem = ({ filter }) => {
  const { clearOneFilter, setFilters, currentFilters } = FilterPanel.useFilterPanel();
  const {
    vehicleClassificationOptions,
  } = useFederalStandards();

  // currentFilters is authoritative; getFilterFromState lags a render behind.
  const valueInState = (key) =>
    currentFilters?.filters?.find((f) => f.key === key)?.value || '';

  const fedStandardCode = valueInState('fedStandardCode');
  const vehicleGroup = valueInState('vehicleGroup');
  const vehicleType = valueInState(filter.key);

  // ignore a group the code no longer owns, so both clear in the same pass
  const appliedVehicleGroup = useMemo(() => {
    const belongsToFedStandardCode = (vehicleClassificationOptions?.vehicleGroup || []).some(
      (vGroup) => vGroup.value === vehicleGroup && vGroup.parent === fedStandardCode,
    );
    return belongsToFedStandardCode ? vehicleGroup : '';
  }, [fedStandardCode, vehicleGroup, vehicleClassificationOptions]);

  const options = useMemo(() => {
    if (!appliedVehicleGroup || !vehicleClassificationOptions?.vehicleType) {
      return [SELECT_VEHICLE_TYPE];
    }

    // only types under the applied vehicle group
    const filteredTypes = vehicleClassificationOptions.vehicleType
      .filter((vType) => vType.parent === appliedVehicleGroup)
      .map((vType) => ({
        label: vType.label,
        value: vType.value,
      }));

    return [SELECT_VEHICLE_TYPE, ...filteredTypes];
  }, [appliedVehicleGroup, vehicleClassificationOptions]);

  // stale once it no longer sits under the applied vehicle group
  const isStale =
    Boolean(vehicleType) &&
    !options.some((option) => option.value === vehicleType);

  // the panel can overwrite a clear while syncing to the url, so re-assert it
  useEffect(() => {
    if (isStale) {
      clearOneFilter(filter);
    }
  }, [isStale, currentFilters?.filters]);

  useEffect(() => {
    setFilters({
      type: 'updateOptions',
      filter: {
        ...filter,
        options,
      },
    });
  }, [options]);

  return (
    <div className="margin-top-3">
      <label htmlFor={filter.key} className="usa-label">
        {filter.title}
      </label>
      <FilterPanelField
        key={[filter.key]}
        filter={{
          ...filter,
          type: 'select',
          options,
        }}
      />
    </div>
  );
};

VehicleTypeFilterItem.propTypes = {
  filter: PropTypes.shape(FilterPanelFilterShape).isRequired,
};

export default VehicleTypeFilterItem;
